import { Title } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarRef,
  SimpleSnackBar,
} from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, timeout } from 'rxjs/operators';

import { DeviceDetectorService } from 'ngx-device-detector';
import * as ip from 'ipify2';

import { environment } from 'src/environments/environment';
import {
  ADMIN_CONSTANT,
  RequestPayload,
  ResponseBody,
  GlobalLogout,
  AdminConfig,
  GlobalLogin,
  OrgMap,
  UserAdminLandingFetch,
  GlobalLoginValidateOTP,
  GlobalResetPassword,
  GlobalChangePassword,
  CommunicationInfo,
} from './app.model';
import { PushNotificationService } from './push-notification.service';
import { ApiLoaderComponent } from './shared/components/api-loader/api-loader.component';
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  userData: any;
  private readonly secretKey;
  adminConfig: AdminConfig[];
  selectedOrganization: OrgMap;
  accessCode: 'NO' | 'RO' | 'RW';
  userScreenCode: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private title: Title,
    private router: Router,
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private deviceService: DeviceDetectorService,
    private pushNotification: PushNotificationService,
    private readonly encryptionService: EncryptionService
  ) {
    this.secretKey = environment.encryptionConfig.SECRECTKEY;
  }

  resourceURL(resource: string, assets?: boolean): string {
    return assets ? environment.assets + 'json/' + resource : resource;
  }

  uploadURL(): string {
    return this.resourceURL(environment.upload_url);
  }

  authorizedURL(): string {
    return this.resourceURL(environment.authorized_url);
  }

  unauthorizedURL(): string {
    return this.resourceURL(environment.unauthorized_url);
  }

  // setToken(key: string, data: object | any, persist?: boolean): void {
  //   persist
  //     ? window.localStorage.setItem(key, JSON.stringify(data))
  //     : window.sessionStorage.setItem(key, JSON.stringify(data));
  // }
  setToken(key: string, data: any, persist?: boolean): void {
    try {
      // const compressedData = LZString.compress(JSON.stringify(data)); // Compress data
      const encryptedData = this.encryptionService.Encrypt(JSON.stringify(data), this.secretKey);
      if (encryptedData) {
        persist
          ? localStorage.setItem(key, encryptedData)
          : sessionStorage.setItem(key, encryptedData);
      }
    } catch (error) {
      console.error('Error setting token:', error);
    }
  }

  // getToken(key: string, persist?: boolean): object | any {
  //   return persist
  //     ? JSON.parse(window.localStorage.getItem(key))
  //     : JSON.parse(window.sessionStorage.getItem(key));
  // }
  getToken(key: string, persist?: boolean): any {
    try {
      const storageValue = persist
        ? localStorage.getItem(key)
        : sessionStorage.getItem(key);

      if (!storageValue) return null;

      const decryptedData = this.encryptionService.Decrypt(storageValue, this.secretKey);
      return decryptedData ? JSON.parse(decryptedData) : null;
      // const decompressedData = LZString.decompress(decryptedData);
      // return decompressedData ? JSON.parse(decompressedData) : null;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  deleteToken(key: string, persist?: boolean): void {
    persist
      ? window.localStorage.removeItem(key)
      : window.sessionStorage.removeItem(key);
  }

  setTitle(title: string): void {
    this.title.setTitle(`Admission Portal | ${title}`);
  }

  requestAPI(
    isAuth: boolean,
    operationKey: string,
    subOperationKey: string,
    businessDataObject: any,
    userData: any = null,
    masterData: boolean = true,
    transactionData: boolean = true
  ): Observable<ResponseBody> {
    const resourceURL = isAuth ? this.authorizedURL() : this.unauthorizedURL();
    const { Header, Body } = new RequestPayload(
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_ID),
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_CODE),
      operationKey,
      subOperationKey,
      businessDataObject,
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID),
      userData,
      masterData,
      transactionData
    );

    // return this.http
    //   .post<ResponseBody>(resourceURL, { Header, Body })
    //   .pipe(map((response) => response));
    return this.http.post<ResponseBody>(resourceURL, { Header, Body }).pipe(

      // Timeout: 20 minutes
      timeout(1200000),

      map((response: ResponseBody) => response)

    );
  }

  uploadAPI(formData: FormData, imgInfo: any): Observable<ResponseBody> {
    const resourceURL = this.uploadURL();
    const { Header, Body } = new RequestPayload(
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_ID),
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_CODE),
      `ImageInfoSave`,
      ``,
      imgInfo,
      this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID),
      this.userData,
      true,
      true
    );

    formData.append('businessData', JSON.stringify({ Header, Body }));
    // console.log("formData", formData);
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Content-Type':  'multipart/form-data',
    //   })
    // };
    // const requestOptions = {  headers: httpOptions, reportProgress: true};
    return this.http
      .post<ResponseBody>(resourceURL, formData, { reportProgress: true })
      .pipe(map((response) => response));
  }

  getIP(): Promise<string> {
    // return ip.ipv4();
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve('127.0.0.1');
      }, 1000);
    });
  }

  getAdminSetting(): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_ADMIN_SETTING,
      '',
      null
    );
  }

  adminInit(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_CONFIG)) {
        this.adminConfig = this.getToken(
          ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_CONFIG
        );
        this.userData = this.getToken(
          ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_USER_DATA
        );
        resolve(true);
      } else {
        forkJoin([this.getIP(), this.getAdminSetting()])
          .toPromise()
          .then(
            ([i, r]) => {
              if (r.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
                this.setToken(
                  ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_CONFIG,
                  r.Body.BusinessData.AdminConfig
                );
                this.adminConfig = r.Body.BusinessData.AdminConfig;
                const DeviceId = Math.floor(
                  Math.random() * (Math.pow(999999, 2) - Math.pow(99999, 2)) +
                  Math.pow(99999, 2)
                );
                const IPAddress = i;
                const { DeviceMake, DeviceModel, OSName, OSVersion } = {
                  OSName: this.deviceService.os,
                  DeviceMake: this.deviceService.device,
                  DeviceModel: this.deviceService.browser,
                  OSVersion: this.deviceService.os_version,
                };
                this.userData = {
                  OSName,
                  DeviceId,
                  IPAddress,
                  OSVersion,
                  DeviceMake,
                  DeviceModel,
                  AuthToken: this.getToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_PUSH_NOTIFICATION,
                    true
                  ),
                  // RefreshToken: this.getToken(
                  //   ADMIN_CONSTANT.ADMIN_TOKEN_REFRESH_TOKEN,
                  //   true,
                  // ),
                };
                this.setToken(
                  ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_USER_DATA,
                  this.userData
                );
                resolve(true);
              } else {
                resolve(false);
              }
            },
            (err) => resolve(false)
          )
          .catch((err) => resolve(false));
      }
    });
  }

  onGlobalLogin(_2FA: boolean, payload: GlobalLogin): Observable<ResponseBody> {
    return _2FA
      ? this.requestAPI(false, ADMIN_CONSTANT.GLOBAL_LOGIN, '', payload)
      : this.onGlobalLoginValidateOTP(payload as GlobalLoginValidateOTP);
  }

  onGlobalLoginResendOTP(payload: GlobalLogin): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_LOGIN_RESEND_OTP,
      '',
      payload
    );
  }

  onGlobalLoginValidateOTP(
    payload: GlobalLoginValidateOTP
  ): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_LOGIN_VALIDATE_OTP,
      '',
      payload,
      this.userData
    );
  }


// Forgot Password (with dob)

  onGlobalResetPassword(
    payload: GlobalResetPassword
  ): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_RESET_PASSWORD,
      '',
      payload,
      this.userData
    );
  }
  
  onGlobalResetPasswordValidate(
    payload: GlobalResetPassword
  ): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_RESET_PASSWORD_VALIDATE,
      '',
      payload,
      this.userData
    );
  }

  onGlobalResetPassworResendOTP(payload: GlobalResetPassword): Observable<ResponseBody> {
    return this.requestAPI(
      false,
      ADMIN_CONSTANT.GLOBAL_RESET_PASSWORD_RESEND_OTP,
      '',
      payload
    );
  }



// Change or Reset Password (old password)

  onGlobalChangePassword(
    payload: GlobalChangePassword
  ): Observable<ResponseBody> {
    return this.requestAPI(
      true,
      ADMIN_CONSTANT.GLOBAL_CHANGE_PASSWORD,
      '',
      payload,
      this.userData
    );
  }

  onGlobalChangePasswordValidate(
    payload: GlobalChangePassword
  ): Observable<ResponseBody> {
    return this.requestAPI(
      true,
      ADMIN_CONSTANT.GLOBAL_CHANGE_PASSWORD_VALIDATE,
      '',
      payload,
      this.userData
    );
  }

  onGlobalChangePassworResendOTP(payload: GlobalChangePassword): Observable<ResponseBody> {
    return this.requestAPI(
      true,
      ADMIN_CONSTANT.GLOBAL_CHANGE_PASSWORD_RESEND_OTP,
      '',
      payload
    );
  }



  onUserAdminLandingFetch(): Observable<ResponseBody> {
    const payload: UserAdminLandingFetch = {
      UsrId: Number(this.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID)),
      ObjectType: ADMIN_CONSTANT.USER_OBJECT_TYPE,
    };
    return this.requestAPI(
      true,
      ADMIN_CONSTANT.USER_ADMIN_LANDING_FETCH,
      '',
      payload,
      this.userData
    );
  }

  onGlobalLogout(payload: GlobalLogout): Observable<ResponseBody> {
    return this.requestAPI(
      true,
      ADMIN_CONSTANT.GLOBAL_LOGOUT,
      '',
      payload,
      this.userData
    );
  }

  onLogout(force: boolean = false): void {
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ORGANIZATION);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SUPER_USER_FLAG);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ULINK);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_USER_MENU_SCREEN);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_GLOBAL_USER_INFO);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_REFRESH_TOKEN);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_AUTH_TOKEN);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_CODE);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_ORG_ID);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER);
    this.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_COURSE_SEARCH_PARAMETERS);    
    this.pushNotification.refreshToken();
    this.selectedOrganization = null;
    if (force) {
      sessionStorage.clear();
      window.location.reload();
    } else {
      this.router.navigate(['authentication'], { replaceUrl: true });
    }
  }

  showToast(
    message: string,
    action: string = 'close',
    duration: number = ADMIN_CONSTANT.TOAST_DURATION_MEDIUM
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.snackBar.open(message, action, { duration });
  }

  openLoader(): MatDialogRef<any, any> {
    return this.dialog.open(ApiLoaderComponent, {
      disableClose: true,
      hasBackdrop: true,
      height: '100px',
      width: '100px',
    });
  }

  onUserMessageGenerate(
    payload: CommunicationInfo,
    tranDBFlag: boolean
  ): Observable<ResponseBody> {
    return this.requestAPI(
      true,
      tranDBFlag === true ? 'UserMessageGenerate' : 'GlobalUserMessageGenerate',
      '',
      payload,
      this.userData,
      false,
      true
    );
  }
}
